import { computed, unref } from 'vue'
import { addUnit, isNumber } from '@jdesign-vue/utils'
import { enforceUnit, sum } from '../utils'

import type { CSSProperties } from 'vue'
import type { TableV2Props } from '../table'
import type { UseColumnsReturn } from './use-columns'
import type { UseDataReturn } from './use-data'

type UseStyleProps = {
  columnsTotalWidth: UseColumnsReturn['columnsTotalWidth']
  data: UseDataReturn['data']
  fixedColumnsOnLeft: UseColumnsReturn['fixedColumnsOnLeft']
  fixedColumnsOnRight: UseColumnsReturn['fixedColumnsOnRight']
}

export const useStyles = (
  props: TableV2Props,
  {
    columnsTotalWidth,
    data,
    fixedColumnsOnLeft,
    fixedColumnsOnRight,
  }: UseStyleProps
) => {
  const bodyWidth = computed(() => {
    // const { fixed, width, vScrollbarSize } = props
    const { width, vScrollbarSize } = props
    const ret = width - vScrollbarSize
    // 无论是否固定列，都应该确保 bodyWidth 至少等于列总宽度，以确保滚动条正常显示
    return Math.max(Math.round(unref(columnsTotalWidth)), ret)
  })

  const headerWidth = computed(
    () => unref(bodyWidth) + (props.fixed ? props.vScrollbarSize : 0)
  )

  const mainTableHeight = computed(() => {
    const { height = 0, maxHeight = 0, footerHeight, hScrollbarSize } = props

    // 当传入 maxHeight 时，按 min(height, maxHeight, 内容总高度) 计算，
    // 避免旧实现在传 maxHeight 时完全忽略 height、造成可视行远大于实际可视区。
    if (maxHeight > 0) {
      const _fixedRowsHeight = unref(fixedRowsHeight)
      const _rowsHeight = unref(rowsHeight)
      const _headerHeight = unref(headerHeight)
      const total =
        _headerHeight + _fixedRowsHeight + _rowsHeight + hScrollbarSize

      // 当设置了 maxHeight 时，使用 total 和 maxHeight 的较小值
      // 不再考虑 height（来自 AutoResizer），因为 AutoResizer 的值会随内容变化
      return Math.min(total, maxHeight - footerHeight)
    }

    return height - footerHeight
  })

  const rowsHeight = computed(() => {
    const { rowHeight, estimatedRowHeight } = props
    const _data = unref(data)
    return isNumber(estimatedRowHeight)
      ? _data.length * estimatedRowHeight
      : _data.length * rowHeight
  })

  const fixedTableHeight = computed(() => {
    const { maxHeight } = props
    const tableHeight = unref(mainTableHeight)
    if (isNumber(maxHeight) && maxHeight > 0) return tableHeight

    const totalHeight =
      unref(rowsHeight) + unref(headerHeight) + unref(fixedRowsHeight)

    return Math.min(tableHeight, totalHeight)
  })

  const mapColumn = (column: TableV2Props['columns'][number]) => column.width

  const leftTableWidth = computed(() =>
    sum(unref(fixedColumnsOnLeft).map(mapColumn))
  )

  const rightTableWidth = computed(() =>
    sum(unref(fixedColumnsOnRight).map(mapColumn))
  )

  const headerHeight = computed(() => sum(props.headerHeight))

  const fixedRowsHeight = computed(() => {
    return (props.fixedData?.length || 0) * props.rowHeight
  })

  const windowHeight = computed(() => {
    return unref(mainTableHeight) - unref(headerHeight) - unref(fixedRowsHeight)
  })

  const rootStyle = computed<CSSProperties>(() => {
    // const { style = {}, height, width } = props
    const { style = {}, height, width, maxHeight } = props
    const _height =
      isNumber(maxHeight) && maxHeight > 0
        ? unref(mainTableHeight) + props.footerHeight
        : height

    return enforceUnit({
      ...style,
      height: _height,
      width,
    })
  })

  const footerHeight = computed(() =>
    enforceUnit({ height: props.footerHeight })
  )

  const emptyStyle = computed<CSSProperties>(() => ({
    top: addUnit(unref(headerHeight)),
    bottom: addUnit(props.footerHeight),
    width: addUnit(props.width),
  }))

  return {
    bodyWidth,
    fixedTableHeight,
    mainTableHeight,
    leftTableWidth,
    rightTableWidth,
    headerWidth,
    rowsHeight,
    windowHeight,
    footerHeight,
    emptyStyle,
    rootStyle,
    headerHeight,
  }
}

export type UseStyleReturn = ReturnType<typeof useStyles>
